import { Injectable, inject } from '@angular/core'
import { RootInjectorGuard } from '../guards/root-injector.guard'
import { Route } from '@angular/router'
import { ROUTES } from '../constants/routes'
import { AuthService } from './auth.service'
import { USER_ROLES } from '../constants/auth'

@Injectable({
  providedIn: 'root',
})
export class RouteService extends RootInjectorGuard {
  authService = inject(AuthService)
  private currentRoute: Route | undefined

  constructor() {
    super(RouteService)
  }

  /**
   * Sets the current route to be referenced for the side menu navigation
   * @param {Route} route Current route
   */
  setRoute(route: Route) {
    this.currentRoute = route
  }

  /**
   * Gets the current route
   * @returns {Route} Current route
   */
  getRoute(): Route | undefined {
    return this.currentRoute
  }

  /**
   * Creates a Route object for the current URL and the product children routes
   * @param {string} title Title of the current page
   * @param {string} childPath The url of the child product route
   * @param {string} productId The id of the product
   * @returns {Route} The route object created
   */
  getProductViewRoutes(
    title: string,
    childPath: string,
    productId: string,
  ): Route {
    const path = `/products/${productId}/${childPath}`
    const children = this.getProductViewListWithId(productId)
    return {
      title,
      path,
      children: [
        ROUTES.HOME,
        {
          title: ROUTES.MARKETPLACE.title,
          path: `/${ROUTES.MARKETPLACE.path}`,
        },
        ...children,
      ],
    }
  }

  /**
   * Returns list of Product child routes with the product id injected
   * @param {string} productId The id of the product
   * @returns {Array<Route>} The product child routes
   */
  getProductViewListWithId(productId: string): Array<Route> {
    return [
      {
        title: ROUTES.PRODUCT_OVERVIEW.title,
        path: `/products/${productId}/${ROUTES.PRODUCT_OVERVIEW.path}`,
      },
      {
        title: ROUTES.PRODUCT_COMPONENTS.title,
        path: `/products/${productId}/${ROUTES.PRODUCT_COMPONENTS.path}`,
      },
      {
        title: ROUTES.PRODUCT_REGULATIONS.title,
        path: `/products/${productId}/${ROUTES.PRODUCT_REGULATIONS.path}`,
      },
      {
        title: ROUTES.PRODUCT_INGREDIENTS.title,
        path: `/products/${productId}/${ROUTES.PRODUCT_INGREDIENTS.path}`,
      },
      {
        title: ROUTES.PRODUCT_PLASTICS_DECLARATION.title,
        path: `/products/${productId}/${ROUTES.PRODUCT_PLASTICS_DECLARATION.path}`,
      },
    ]
  }

  /**
   * Creates a Route object for the current URL and the default children routes for different user roles
   * @param {string} title Title of the current page
   * @param {string} path The url of the current page
   * @returns {Route} The route object created
   */
  getUserRoutes(title: string, path: string): Route {
    const routeChildren: Array<Route> = [ROUTES.HOME]
    const session = this.authService.getSessionFromLS()

    // guest users
    if (!session) {
      return {
        title,
        path,
        children: [
          ROUTES.HOME,
          ROUTES.MARKETPLACE,
          ROUTES.INGREDIENTS,
          ROUTES.SETTINGS,
          ROUTES.LIABILITY_DISCLAIMER,
        ],
      }
    }

    switch (session.user.role) {
      case USER_ROLES.MASTER:
        routeChildren.push(
          {
            title: ROUTES.USERS.title,
            path: `/admin/${ROUTES.USERS.path}`,
          },
          {
            title: ROUTES.COMPANY.title,
            path: `/admin/${ROUTES.COMPANY.path}`,
          },
        )
        break
      case USER_ROLES.ADMIN:
        routeChildren.push(
          {
            title: ROUTES.USERS.title,
            path: `/admin/${ROUTES.USERS.path}`,
          },
          {
            title: ROUTES.INTEGRATIONS.title,
            path: `/admin/${ROUTES.INTEGRATIONS.path}`,
          },
          {
            title: ROUTES.COMPANY_PRODUCTS.title,
            path: `/admin/${ROUTES.COMPANY.path}/${ROUTES.COMPANY_PRODUCTS.path}`,
          },
          {
            title: ROUTES.REPORTS.title,
            path: `/admin/${ROUTES.REPORTS.path}`,
          },
        )
        break
      case USER_ROLES.COMPLIANCE_OFFICER:
        routeChildren.push(
          {
            title: ROUTES.INTEGRATIONS.title,
            path: `/admin/${ROUTES.INTEGRATIONS.path}`,
          },
          {
            title: ROUTES.COMPANY_PRODUCTS.title,
            path: `/admin/${ROUTES.COMPANY.path}/${ROUTES.COMPANY_PRODUCTS.path}`,
          },
          {
            title: ROUTES.REPORTS.title,
            path: `/admin/${ROUTES.REPORTS.path}`,
          },
        )
        break
    }

    routeChildren.push(ROUTES.MARKETPLACE)
    if (
      [USER_ROLES.ADMIN, USER_ROLES.COMPLIANCE_OFFICER].includes(
        session.user.role,
      )
    ) {
      routeChildren.push(
        ROUTES.REQUISITION_CART,
        ROUTES.REQUISITIONS,
        ROUTES.PURCHASE_ORDERS,
      )
    }
    routeChildren.push(
      ROUTES.INGREDIENTS,
      ROUTES.SETTINGS,
      ROUTES.LIABILITY_DISCLAIMER,
    )

    return {
      title,
      path,
      children: routeChildren,
    }
  }

  /**
   * Creates a Route object for the current URL and the default children routes for companies
   * @param {string} title Title of the current page
   * @param {string} path The url of the current page
   * @returns {Route} The route object created
   */
  getCompanyRoutes(title: string, path: string): Route {
    return {
      title,
      path,
      children: [
        {
          title: ROUTES.CREATE_COMPANY.title,
          path: `/admin/companies/${ROUTES.CREATE_COMPANY.path}`,
        },
        {
          title: ROUTES.COMPANY_PRODUCTS.title,
          path: `/companies/${ROUTES.COMPANY_PRODUCTS.path}`,
        },
      ],
    }
  }
}
